/**
 * Route Configuration
 * 
 * Defines all application routes with nested structure and lazy loading.
 * Routes are organized by user role (admin, user, external).
 */

import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleRoute } from './RoleRoute';
import { RouteError } from './RouteError';

// Lazy load layout
const AppLayout = lazy(() => import('@/components/layout/AppLayout'));

// Lazy load auth pages
const CallbackPage = lazy(() => import('@/pages/auth/CallbackPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));

// Lazy load admin pages
const SystemUsersPage = lazy(() => import('@/pages/admin/SystemUsersPage'));
const SystemUserDetailPage = lazy(() => import('@/pages/admin/SystemUserDetailPage'));
const AppUsersPage = lazy(() => import('@/pages/admin/AppUsersPage'));
const AppUserDetailPage = lazy(() => import('@/pages/admin/AppUserDetailPage'));
const AppUserEditPage = lazy(() => import('@/pages/admin/AppUserEditPage'));
const UserCreatePage = lazy(() => import('@/pages/admin/UserCreatePage'));
const UserEditPage = lazy(() => import('@/pages/admin/UserEditPage'));
const NotificationsPage = lazy(() => import('@/pages/admin/NotificationsPage'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));

// Lazy load user pages
const HomePage = lazy(() => import('@/pages/user/HomePage'));
const ContractsPage = lazy(() => import('@/pages/user/ContractsPage'));
const ContractDetailPage = lazy(() => import('@/pages/user/ContractDetailPage'));
const ContractCreatePage = lazy(() => import('@/pages/user/ContractCreatePage'));
const ContractEditPage = lazy(() => import('@/pages/user/ContractEditPage'));
const MyContractsPage = lazy(() => import('@/pages/user/MyContractsPage'));
const AgenciesPage = lazy(() => import('@/pages/user/AgenciesPage'));
const AgencyDetailPage = lazy(() => import('@/pages/user/AgencyDetailPage'));
const AgencyCreatePage = lazy(() => import('@/pages/user/AgencyCreatePage'));
const AgencyEditPage = lazy(() => import('@/pages/user/AgencyEditPage'));
const FilesPage = lazy(() => import('@/pages/user/FilesPage'));
const DashboardsPage = lazy(() => import('@/pages/user/DashboardsPage'));
const ReportsPage = lazy(() => import('@/pages/user/ReportsPage'));
const ProfilePage = lazy(() => import('@/pages/user/ProfilePage'));
const TenantsPage = lazy(() => import('@/pages/user/TenantsPage'));
const RecyclingBinPage = lazy(() => import('@/pages/user/RecyclingBinPage'));
const NotificationInboxPage = lazy(() => import('@/pages/user/NotificationInboxPage'));

// Lazy load external client pages
// TODO: Implement external client pages or remove these routes
// const PaymentHistoryPage = lazy(() => import('@/pages/external/PaymentHistoryPage'));
// const TimelinePage = lazy(() => import('@/pages/external/TimelinePage'));

/**
 * Application router configuration
 * 
 * Route structure:
 * - /callback → OAuth callback (no layout)
 * - /register → User registration (no layout)
 * - / → App with layout
 *   - /home, /contracts, /agencies, etc. → User routes
 *   - /admin/* → Admin-only routes
 *   - /external/* → External client routes
 */
export const router = createBrowserRouter([
  // Public routes without layout
  {
    path: '/callback',
    element: <CallbackPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },

  // App routes with layout
  {
    path: '/',
    element: <AppLayout />,
    // Last resort: a crash in the layout itself
    errorElement: <RouteError />,
    children: [
      // Root redirect
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },

      // Protected routes (require authentication)
      {
        element: <ProtectedRoute />,
        // A page crash renders here, inside the layout, so the menu keeps working
        errorElement: <RouteError />,
        children: [
          // Admin routes (Administrador only)
          {
            element: <RoleRoute allowedRoles={['Administrador']} />,
            children: [
              {
                path: 'admin',
                children: [
                  {
                    path: 'users',
                    children: [
                      {
                        index: true,
                        element: <SystemUsersPage />,
                      },
                      {
                        path: 'new',
                        element: <UserCreatePage />,
                      },
                      {
                        path: ':username',
                        element: <SystemUserDetailPage />,
                      },
                      {
                        path: ':username/edit',
                        element: <UserEditPage />,
                      },
                    ],
                  },
                  {
                    path: 'app-users',
                    children: [
                      {
                        index: true,
                        element: <AppUsersPage />,
                      },
                      {
                        path: ':id',
                        element: <AppUserDetailPage />,
                      },
                      {
                        path: ':id/edit',
                        element: <AppUserEditPage />,
                      },
                    ],
                  },
                  {
                    path: 'notifications',
                    element: <NotificationsPage />,
                  },
                  {
                    path: 'dashboard',
                    element: <AdminDashboard />,
                  },
                ],
              },
            ],
          },

          // User routes (Administrador, Coordinador, Operativo, Asesor)
          {
            element: <RoleRoute allowedRoles={['Administrador', 'Coordinador', 'Operativo', 'Asesor']} />,
            children: [
              {
                path: 'home',
                element: <HomePage />,
              },
              {
                path: 'contracts',
                children: [
                  {
                    index: true,
                    element: <ContractsPage />,
                  },
                  {
                    path: 'new',
                    element: <ContractCreatePage />,
                  },
                  {
                    path: ':id',
                    element: <ContractDetailPage />,
                  },
                  {
                    path: ':id/edit',
                    element: <ContractEditPage />,
                  },
                ],
              },
              {
                path: 'my-contracts',
                element: <MyContractsPage />,
              },
              {
                path: 'agencies',
                children: [
                  {
                    index: true,
                    element: <AgenciesPage />,
                  },
                  {
                    path: 'new',
                    element: <AgencyCreatePage />,
                  },
                  {
                    path: ':id',
                    element: <AgencyDetailPage />,
                  },
                  {
                    path: ':id/edit',
                    element: <AgencyEditPage />,
                  },
                ],
              },
              {
                path: 'files',
                element: <FilesPage />,
              },
              {
                path: 'recycling-bin',
                element: <RecyclingBinPage />,
              },
              {
                path: 'dashboards',
                element: <DashboardsPage />,
              },
              {
                path: 'reports',
                element: <ReportsPage />,
              },
              {
                path: 'profile',
                element: <ProfilePage />,
              },
              {
                path: 'tenants',
                element: <TenantsPage />,
              },
              {
                path: 'notifications',
                element: <NotificationInboxPage />,
              },
            ],
          },

          // External client routes (External role)
          // TODO: Implement external client pages or remove these routes
          // {
          //   element: <RoleRoute allowedRoles={['External']} />,
          //   children: [
          //     {
          //       path: 'external',
          //       children: [
          //         {
          //           path: 'payments',
          //           element: <PaymentHistoryPage />,
          //         },
          //         {
          //           path: 'timeline',
          //           element: <TimelinePage />,
          //         },
          //       ],
          //     },
          //   ],
          // },
        ],
      },

      // 404 catch-all
      {
        path: '*',
        element: <Navigate to="/home" replace />,
      },
    ],
  },
]);
